import * as React from 'react';
import {
  Star,
  Search,
  X,
  ArrowUp,
  ArrowDown,
  Filter,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { MultiSelect } from '@/components/ui/multiselect';
import { Separator } from '@/components/ui/separator';
import type { Client } from '@/types';

type EnrichedCustomer = Client & {
  isFavourite?: boolean;
  lastAccessedDate?: Date;
};

interface CustomerSearchModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customers: EnrichedCustomer[];
  value?: string;
  onSelect: (customerId: string) => void;
  onToggleFavourite: (customerId: string) => void;
  isFavourite: (customerId: string) => boolean;
  selectedCustomerId?: string;
}

type SortColumn = 'name' | 'code' | 'address';
type SortDirection = 'asc' | 'desc' | null;

const SEARCH_DEBOUNCE_MS = 300;
const ITEMS_PER_PAGE = 50;

function formatAddress(customer: Client): string {
  const parts = [
    customer.streetAddress,
    customer.city,
    customer.county,
    customer.postcode,
    customer.country,
  ].filter(Boolean);
  return parts.join(', ') || '-';
}

export function CustomerSearchModal({
  open,
  onOpenChange,
  customers,
  value,
  onSelect,
  onToggleFavourite,
  isFavourite,
  selectedCustomerId,
}: CustomerSearchModalProps) {
  const [searchQuery, setSearchQuery] = React.useState('');
  const [debouncedSearch, setDebouncedSearch] = React.useState('');
  const [sortColumn, setSortColumn] = React.useState<SortColumn>('name');
  const [sortDirection, setSortDirection] = React.useState<SortDirection>('asc');
  const [showFilters, setShowFilters] = React.useState(false);
  const [displayedItems, setDisplayedItems] = React.useState(ITEMS_PER_PAGE);

  // Filter states (pending until "Apply Filters" is clicked)
  const [pendingShowFavouritesOnly, setPendingShowFavouritesOnly] = React.useState(false);
  const [pendingCodeFilter, setPendingCodeFilter] = React.useState<string[]>([]);
  const [pendingNameFilter, setPendingNameFilter] = React.useState<string[]>([]);

  // Applied filters (actually used for filtering)
  const [showFavouritesOnly, setShowFavouritesOnly] = React.useState(false);
  const [codeFilter, setCodeFilter] = React.useState<string[]>([]);
  const [nameFilter, setNameFilter] = React.useState<string[]>([]);

  // Debounce search
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Reset on close
  React.useEffect(() => {
    if (!open) {
      setSearchQuery('');
      setDebouncedSearch('');
      setSortColumn('name');
      setSortDirection('asc');
      setShowFilters(false);
      setDisplayedItems(ITEMS_PER_PAGE);
      setShowFavouritesOnly(false);
      setPendingShowFavouritesOnly(false);
      setCodeFilter([]);
      setPendingCodeFilter([]);
      setNameFilter([]);
      setPendingNameFilter([]);
    }
  }, [open]);

  // Fuzzy search function
  const fuzzyMatch = React.useCallback((text: string, query: string): boolean => {
    if (!text) return false;
    const textLower = text.toLowerCase();
    const queryLower = query.toLowerCase();
    if (textLower.includes(queryLower)) return true;
    let queryIndex = 0;
    for (let i = 0; i < textLower.length && queryIndex < queryLower.length; i++) {
      if (textLower[i] === queryLower[queryIndex]) {
        queryIndex++;
      }
    }
    return queryIndex === queryLower.length;
  }, []);

  // Get unique values for filters
  const uniqueCodes = React.useMemo(() => {
    const codes = new Set<string>();
    customers.forEach(c => {
      const code = c.code || String(c.id);
      if (code) codes.add(code);
    });
    return Array.from(codes).sort();
  }, [customers]);

  const uniqueNames = React.useMemo(() => {
    const names = new Set<string>();
    customers.forEach(c => {
      if (c.name) names.add(c.name);
    });
    return Array.from(names).sort();
  }, [customers]);

  // Filter and search customers
  const filteredCustomers = React.useMemo(() => {
    let filtered = [...customers];

    // Apply favourites filter
    if (showFavouritesOnly) {
      filtered = filtered.filter(c => isFavourite(String(c.id)));
    }

    // Apply customer code filter
    if (codeFilter.length > 0) {
      filtered = filtered.filter(c =>
        codeFilter.includes(c.code || String(c.id))
      );
    }

    // Apply customer name filter
    if (nameFilter.length > 0) {
      filtered = filtered.filter(c =>
        nameFilter.includes(c.name)
      );
    }

    // Apply search
    if (debouncedSearch) {
      filtered = filtered.filter(customer => {
        return (
          fuzzyMatch(customer.name, debouncedSearch) ||
          fuzzyMatch(customer.code || String(customer.id), debouncedSearch) ||
          fuzzyMatch(formatAddress(customer), debouncedSearch)
        );
      });
    }

    return filtered;
  }, [customers, debouncedSearch, showFavouritesOnly, codeFilter, nameFilter, fuzzyMatch, isFavourite]);

  // Sort customers
  const sortedCustomers = React.useMemo(() => {
    if (!sortDirection) return filteredCustomers;

    return [...filteredCustomers].sort((a, b) => {
      let aValue: string;
      let bValue: string;

      switch (sortColumn) {
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'code':
          aValue = (a.code || String(a.id)).toLowerCase();
          bValue = (b.code || String(b.id)).toLowerCase();
          break;
        case 'address':
          aValue = formatAddress(a).toLowerCase();
          bValue = formatAddress(b).toLowerCase();
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredCustomers, sortColumn, sortDirection]);

  // Paginated customers for infinite scroll
  const displayedCustomers = React.useMemo(() => {
    return sortedCustomers.slice(0, displayedItems);
  }, [sortedCustomers, displayedItems]);

  // Handle column header click for sorting
  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        setSortDirection(null);
        setSortColumn('name');
      } else {
        setSortDirection('asc');
      }
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Handle customer selection
  const handleSelect = (customerId: string) => {
    if (String(customerId) === String(selectedCustomerId)) {
      onOpenChange(false);
      return;
    }

    onSelect(customerId);
    onOpenChange(false);
  };

  // Handle star click
  const handleStarClick = (e: React.MouseEvent, customerId: string) => {
    e.stopPropagation();
    e.preventDefault();
    onToggleFavourite(customerId);
  };

  // Apply filters
  const handleApplyFilters = () => {
    setShowFavouritesOnly(pendingShowFavouritesOnly);
    setCodeFilter(pendingCodeFilter);
    setNameFilter(pendingNameFilter);
  };

  // Clear all filters
  const handleClearFilters = () => {
    setPendingShowFavouritesOnly(false);
    setShowFavouritesOnly(false);
    setPendingCodeFilter([]);
    setCodeFilter([]);
    setPendingNameFilter([]);
    setNameFilter([]);
  };

  // Load more items for infinite scroll
  const handleLoadMore = () => {
    setDisplayedItems(prev => prev + ITEMS_PER_PAGE);
  };

  // Render sort icon
  const renderSortIcon = (column: SortColumn) => {
    if (sortColumn !== column || !sortDirection) {
      return null;
    }
    return sortDirection === 'asc' ? (
      <ArrowUp className="ml-1 h-4 w-4" />
    ) : (
      <ArrowDown className="ml-1 h-4 w-4" />
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] h-[90vh] p-0 gap-0 flex flex-col">
        <DialogHeader className="px-4 py-3 border-b shrink-0">
          <DialogTitle className="text-base">Select Customer</DialogTitle>
        </DialogHeader>

        <div className="flex flex-1 overflow-hidden min-h-0">
          {/* Main content */}
          <div className="flex-1 flex flex-col overflow-hidden min-h-0">
            {/* Search bar */}
            <div className="px-4 py-3 border-b shrink-0">
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search customers..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 pr-9"
                  />
                  {searchQuery && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                      onClick={() => setSearchQuery('')}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setShowFilters(!showFilters)}
                  className={cn("relative", showFilters ? "bg-muted" : "")}
                >
                  <Filter className="h-4 w-4" />
                  {(showFavouritesOnly || codeFilter.length > 0 || nameFilter.length > 0) && (
                    <span className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-primary" />
                  )}
                </Button>
              </div>
              <div className="mt-2 text-sm text-muted-foreground">
                {sortedCustomers.length} customer{sortedCustomers.length !== 1 ? 's' : ''} found
              </div>
            </div>

            {/* Data table with scroll */}
            <div className="flex-1 overflow-auto min-h-0">
              <div className="min-w-max px-4">
                <Table>
                  <TableHeader className="sticky top-0 bg-background z-10">
                    <TableRow>
                      <TableHead className="w-[50px] bg-background">
                        <Star className="h-4 w-4 text-muted-foreground" />
                      </TableHead>
                      <TableHead
                        className="cursor-pointer hover:bg-muted/50 bg-background min-w-[150px]"
                        onClick={() => handleSort('code')}
                      >
                        <div className="flex items-center">
                          Customer Code
                          {renderSortIcon('code')}
                        </div>
                      </TableHead>
                      <TableHead
                        className="cursor-pointer hover:bg-muted/50 bg-background min-w-[200px]"
                        onClick={() => handleSort('name')}
                      >
                        <div className="flex items-center">
                          Name
                          {renderSortIcon('name')}
                        </div>
                      </TableHead>
                      <TableHead
                        className="cursor-pointer hover:bg-muted/50 bg-background min-w-[350px]"
                        onClick={() => handleSort('address')}
                      >
                        <div className="flex items-center">
                          Customer Address
                          {renderSortIcon('address')}
                        </div>
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {displayedCustomers.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                          No customers match these results. Please try another filter/search term.
                        </TableCell>
                      </TableRow>
                    ) : (
                      <>
                        {displayedCustomers.map((customer) => (
                          <TableRow
                            key={customer.id}
                            className={cn(
                              'cursor-pointer hover:bg-muted/50',
                              String(customer.id) === String(selectedCustomerId) && 'bg-primary/10'
                            )}
                            onClick={() => handleSelect(String(customer.id))}
                          >
                            <TableCell onClick={(e) => e.stopPropagation()}>
                              <Star
                                className={cn(
                                  'h-4 w-4 cursor-pointer transition-colors',
                                  isFavourite(String(customer.id))
                                    ? 'fill-yellow-400 text-yellow-400'
                                    : 'text-muted-foreground hover:text-yellow-400'
                                )}
                                onClick={(e) => handleStarClick(e, String(customer.id))}
                              />
                            </TableCell>
                            <TableCell>{customer.code || String(customer.id)}</TableCell>
                            <TableCell className="font-medium">{customer.name}</TableCell>
                            <TableCell>{formatAddress(customer)}</TableCell>
                          </TableRow>
                        ))}
                      </>
                    )}
                  </TableBody>
                </Table>

                {/* Load more button for infinite scroll */}
                {displayedCustomers.length < sortedCustomers.length && (
                  <div className="flex justify-center py-4">
                    <Button variant="outline" onClick={handleLoadMore}>
                      Load More ({sortedCustomers.length - displayedCustomers.length} remaining)
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Advanced filters sidebar */}
          {showFilters && (
            <>
              <Separator orientation="vertical" />
              <div className="w-[300px] border-l flex flex-col min-h-0">
                <div className="px-4 py-2.5 border-b flex items-center justify-between shrink-0">
                  <h3 className="font-semibold text-sm">Filters</h3>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0"
                    onClick={() => setShowFilters(false)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>

                <div className="flex-1 overflow-y-auto min-h-0 px-4 py-3">
                  <div className="space-y-4">
                    {/* Show Favourites Only */}
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Favourites</Label>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="customer-favourites"
                          checked={pendingShowFavouritesOnly}
                          onCheckedChange={(checked) => setPendingShowFavouritesOnly(checked === true)}
                        />
                        <label
                          htmlFor="customer-favourites"
                          className="text-sm font-normal leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                        >
                          Show only favourites
                        </label>
                      </div>
                    </div>

                    {/* Customer Code filter */}
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Customer Code</Label>
                      <MultiSelect
                        options={uniqueCodes.map(c => ({ label: c, value: c }))}
                        value={pendingCodeFilter}
                        onValueChange={setPendingCodeFilter}
                        placeholder="Select codes..."
                      />
                    </div>

                    {/* Customer Name filter */}
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Customer Name</Label>
                      <MultiSelect
                        options={uniqueNames.map(n => ({ label: n, value: n }))}
                        value={pendingNameFilter}
                        onValueChange={setPendingNameFilter}
                        placeholder="Select names..."
                      />
                    </div>
                  </div>
                </div>

                <div className="px-4 py-2.5 border-t space-y-2 shrink-0">
                  <Button className="w-full" size="sm" onClick={handleApplyFilters}>
                    Apply Filters
                  </Button>
                  <Button variant="outline" className="w-full" size="sm" onClick={handleClearFilters}>
                    Clear All Filters
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
